import React, { useState } from 'react';
import { useZones } from '../hooks/useZones';
import { useBoxes } from '../hooks/useBoxes';

interface PrintCodesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PrintCodesModal: React.FC<PrintCodesModalProps> = ({ isOpen, onClose }) => {
  const [selectedType, setSelectedType] = useState<'zones' | 'boxes'>('zones');
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(true);

  const { data: zones = [], isLoading: zonesLoading } = useZones();
  const { data: boxes = [], isLoading: boxesLoading } = useBoxes();

  if (!isOpen) return null;

  const items = selectedType === 'zones' ? zones : boxes;
  const isLoading = selectedType === 'zones' ? zonesLoading : boxesLoading;

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                🖨️ Imprimer des Codes-Barres
              </h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                <span className="text-2xl">&times;</span>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type de codes à imprimer
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setSelectedType('zones')}
                    className={`p-4 border-2 rounded-lg transition-all ${
                      selectedType === 'zones'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div className="text-3xl mb-2">🏠</div>
                    <div className="text-sm font-medium text-gray-900">Zones</div>
                    <div className="text-xs text-gray-500 mt-1">Armoires, étagères</div>
                  </button>
                  <button
                    onClick={() => setSelectedType('boxes')}
                    className={`p-4 border-2 rounded-lg transition-all ${
                      selectedType === 'boxes'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div className="text-3xl mb-2">📦</div>
                    <div className="text-sm font-medium text-gray-900">Boxes</div>
                    <div className="text-xs text-gray-500 mt-1">Boxes mobiles</div>
                  </button>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <p className="text-sm text-yellow-800">
                  💡 Les codes-barres seront générés et affichés dans une vue prête à imprimer.
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Sélection
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={(e) => {
                        setSelectAll(e.target.checked);
                        if (e.target.checked) {
                          setSelectedItems([]);
                        }
                      }}
                      className="form-checkbox text-blue-600"
                    />
                    <span>Tout sélectionner</span>
                  </label>
                </div>
                
                {!selectAll && (
                  <div className="border border-gray-300 rounded-md max-h-48 overflow-y-auto">
                    {isLoading ? (
                      <div className="p-4 text-center text-gray-500">Chargement...</div>
                    ) : items.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">
                        Aucun {selectedType === 'zones' ? 'zone' : 'box'} disponible
                      </div>
                    ) : (
                      items.map((item: any) => (
                        <label
                          key={item.id}
                          className="flex items-center gap-2 p-2 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            value={item.id}
                            checked={selectedItems.includes(item.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedItems([...selectedItems, item.id]);
                              } else {
                                setSelectedItems(selectedItems.filter(id => id !== item.id));
                              }
                            }}
                            className="form-checkbox text-blue-600"
                          />
                          <span className="text-sm">
                            <span className="font-medium">{item.code}</span> - {item.nom}
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                )}
                
                <p className="text-xs text-gray-500 mt-2">
                  {selectAll
                    ? `Tous les ${selectedType === 'zones' ? 'zones' : 'boxes'} seront imprimés (${items.length} au total)`
                    : `${selectedItems.length} ${selectedType === 'zones' ? 'zone(s)' : 'box(es)'} sélectionné(s)`
                  }
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              onClick={handlePrint}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-purple-600 text-base font-medium text-white hover:bg-purple-700 sm:ml-3 sm:w-auto sm:text-sm"
            >
              🖨️ Imprimer
            </button>
            <button
              onClick={onClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Annuler
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintCodesModal;

